/**
 * Suno AI Music Generation Service via KIE.AI API
 * Documentation: https://docs.kie.ai/suno-api/quickstart
 */

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'https://api.daggpt.network'}/api/suno`;

/**
 * Generate music from text prompt
 * @param {Object} options - Generation options
 * @param {string} options.prompt - Music description
 * @param {boolean} options.customMode - Use custom mode (requires style and title)
 * @param {boolean} options.instrumental - Generate instrumental only
 * @param {string} options.model - AI model (V3_5, V4, V4_5, V4_5PLUS, V5)
 * @param {string} options.style - Music style (required in custom mode)
 * @param {string} options.title - Song title (required in custom mode)
 * @param {string} options.negativeTags - Tags to avoid
 * @returns {Promise<string>} - Task ID
 */
export const generateMusic = async (options) => {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (options.userId) headers['x-user-id'] = options.userId;

    const response = await fetch(`${API_BASE_URL}/generate`, {
      method: 'POST',
      headers,
      body: JSON.stringify(options)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Music generation failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Suno music generation error:', error);
    throw error;
  }
};


/**
 * Extend existing music track
 * @param {Object} options - Extension options
 * @param {string} options.audioId - ID of audio to extend
 * @param {boolean} options.defaultParamFlag - Use default parameters
 * @param {string} options.model - AI model
 * @param {string} options.prompt - Extension description
 * @param {string} options.style - Music style
 * @param {string} options.title - Extended track title
 * @param {number} options.continueAt - Continue from timestamp (seconds)
 * @returns {Promise<string>} - Task ID
 */
export const extendMusic = async (options) => {
  try {
    const response = await fetch(`${API_BASE_URL}/extend`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(options)
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Music extension failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Suno music extension error:', error);
    throw error;
  }
};

/**
 * Generate lyrics from prompt
 * @param {string} prompt - Lyrics description
 * @returns {Promise<string>} - Task ID
 */
export const generateLyrics = async (prompt) => {
  try {
    const response = await fetch(`${API_BASE_URL}/lyrics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ prompt })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Lyrics generation failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Suno lyrics generation error:', error);
    throw error;
  }
};

/**
 * Check task status
 * @param {string} taskId - Task ID to check
 * @returns {Promise<Object>} - Task status and data
 */
export const checkStatus = async (taskId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/status/${taskId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Status check failed');
    }

    const result = await response.json();
    console.log('📊 Raw status result:', result);
    
    // Kie.ai returns {code: 200, msg: "success", data: {...}}
    // We need to return the data object which contains {status, response, errorMessage}
    return result.data || result;
  } catch (error) {
    console.error('❌ Suno status check error:', error);
    throw error;
  }
};

/**
 * Wait for task completion with polling
 * @param {string} taskId - Task ID to wait for
 * @param {function} onProgress - Progress callback
 * @param {number} maxWaitTime - Maximum wait time in ms (default: 10 minutes)
 * @returns {Promise<Object>} - Completed task data
 */
export const waitForCompletion = async (taskId, onProgress, maxWaitTime = 600000) => {
  const startTime = Date.now();
  const pollInterval = 5000;

  while (Date.now() - startTime < maxWaitTime) {
    const status = await checkStatus(taskId);

    if (onProgress) {
      onProgress(status);
    }

    switch (status.status) {
      case 'SUCCESS':
        console.log('✅ All tracks generated successfully!');
        return status.response;

      case 'FIRST_SUCCESS':
        console.log('✅ First track generation completed!');
        return status.response;

      case 'TEXT_SUCCESS':
        console.log('✅ Lyrics/text generation successful!');
        return status.response;

      case 'PENDING':
        console.log('⏳ Task is pending...');
        break;

      case 'CREATE_TASK_FAILED':
        const createError = status.errorMessage || 'Task creation failed';
        console.error('❌ Error:', createError);
        throw new Error(createError);

      case 'GENERATE_AUDIO_FAILED':
        const audioError = status.errorMessage || 'Audio generation failed';
        console.error('❌ Error:', audioError);
        throw new Error(audioError);

      case 'CALLBACK_EXCEPTION':
        const callbackError = status.errorMessage || 'Callback process error';
        console.error('❌ Error:', callbackError);
        throw new Error(callbackError);

      case 'SENSITIVE_WORD_ERROR':
        const sensitiveError = status.errorMessage || 'Content filtered due to sensitive words';
        console.error('❌ Error:', sensitiveError);
        throw new Error(sensitiveError);

      default:
        console.log(`❓ Unknown status: ${status.status}`);
        if (status.errorMessage) {
          console.error('❌ Error message:', status.errorMessage);
        }
        break;
    }

    await new Promise(resolve => setTimeout(resolve, pollInterval));
  }

  throw new Error('Generation timeout - exceeded maximum wait time');
};

/**
 * Boost/enhance music style (V4_5 models only)
 * @param {string} content - Style description to enhance
 * @returns {Promise<string>} - Enhanced style
 */
export const boostMusicStyle = async (content) => {
  try {
    const response = await fetch(`${API_BASE_URL}/boost-style`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ content })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Style boost failed');
    }

    const result = await response.json();
    return result.enhancedStyle;
  } catch (error) {
    console.error('❌ Suno style boost error:', error);
    throw error;
  }
};

/**
 * Upload and create cover version of audio
 */
export const uploadAndCover = async (audioFile, options) => {
  try {
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('prompt', options.prompt || '');
    formData.append('model', options.model || 'V4');
    formData.append('style', options.style || '');
    formData.append('title', options.title || '');

    const response = await fetch(`${API_BASE_URL}/upload-cover`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload and cover failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Upload and cover error:', error);
    throw error;
  }
};

/**
 * Upload and extend audio
 */
export const uploadAndExtend = async (audioFile, options) => {
  try {
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('prompt', options.prompt || '');
    formData.append('model', options.model || 'V4');
    formData.append('continueAt', options.continueAt || 0);

    const response = await fetch(`${API_BASE_URL}/upload-extend`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload and extend failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Upload and extend error:', error);
    throw error;
  }
};

/**
 * Add instrumental to audio
 */
export const addInstrumental = async (audioFile) => {
  try {
    const formData = new FormData();
    formData.append('audio', audioFile);

    const response = await fetch(`${API_BASE_URL}/add-instrumental`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Add instrumental failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Add instrumental error:', error);
    throw error;
  }
};

/**
 * Add vocals to audio
 */
export const addVocals = async (audioFile) => {
  try {
    const formData = new FormData();
    formData.append('audio', audioFile);

    const response = await fetch(`${API_BASE_URL}/add-vocals`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Add vocals failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Add vocals error:', error);
    throw error;
  }
};

/**
 * Generate cover image for music
 */
export const generateCover = async (audioId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/generate-cover`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audioId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Cover generation failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Cover generation error:', error);
    throw error;
  }
};

/**
 * Replace section of music
 */
export const replaceSection = async (audioId, startTime, endTime, prompt) => {
  try {
    const response = await fetch(`${API_BASE_URL}/replace-section`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audioId, startTime, endTime, prompt })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Section replacement failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Section replacement error:', error);
    throw error;
  }
};

/**
 * Create mashup from multiple tracks
 */
export const createMashup = async (audioIds, prompt, model = 'V4') => {
  try {
    const response = await fetch(`${API_BASE_URL}/mashup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audioIds, prompt, model })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Mashup creation failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Mashup creation error:', error);
    throw error;
  }
};

/**
 * Get timestamped lyrics
 */
export const getTimestampedLyrics = async (audioId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/timestamped-lyrics/${audioId}`);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get timestamped lyrics');
    }

    const result = await response.json();
    return result.lyrics;
  } catch (error) {
    console.error('❌ Get timestamped lyrics error:', error);
    throw error;
  }
};

/**
 * Convert to WAV format
 */
export const convertToWAV = async (audioId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/convert-wav`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audioId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'WAV conversion failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ WAV conversion error:', error);
    throw error;
  }
};

/**
 * Separate vocals and instruments
 */
export const separateVocals = async (audioId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/separate-vocals`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audioId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Vocal separation failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Vocal separation error:', error);
    throw error;
  }
};

/**
 * Generate MIDI from audio
 */
export const generateMIDI = async (audioId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/generate-midi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ audioId })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'MIDI generation failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ MIDI generation error:', error);
    throw error;
  }
};

/**
 * Create music video
 */
export const createMusicVideo = async (audioId, musicTaskId, userId, trackTitle) => {
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (userId) headers['x-user-id'] = userId;

    const response = await fetch(`${API_BASE_URL}/create-video`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ audioId, musicTaskId, trackTitle })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Music video creation failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Music video creation error:', error);
    throw error;
  }
};


/**
 * Poll Kei.AI music video task status
 * Uses dedicated /mp4/record-info endpoint (separate from music track status)
 */
export const checkMusicVideoStatus = async (taskId) => {
  const response = await fetch(`${API_BASE_URL}/video-status/${taskId}`);
  if (!response.ok) throw new Error('Failed to check music video status');
  return response.json();
};


/**
 * Generate sound effects
 */
export const generateSounds = async (prompt, options = {}) => {
  try {
    const response = await fetch(`${API_BASE_URL}/generate-sounds`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        prompt,
        loop: options.loop || false,
        bpm: options.bpm || 120,
        key: options.key || 'C',
        model: options.model || 'V4'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Sound generation failed');
    }

    const result = await response.json();
    return result.taskId;
  } catch (error) {
    console.error('❌ Sound generation error:', error);
    throw error;
  }
};

export default {
  generateMusic,
  extendMusic,
  generateLyrics,
  checkStatus,
  waitForCompletion,
  boostMusicStyle,
  uploadAndCover,
  uploadAndExtend,
  addInstrumental,
  addVocals,
  generateCover,
  replaceSection,
  createMashup,
  getTimestampedLyrics,
  convertToWAV,
  separateVocals,
  generateMIDI,
  createMusicVideo,
  generateSounds
};
